import { useSafeAreaInsets } from 'react-native-safe-area-context';

import actions from '../actions';
import {
    entityData,
    Icon,
    Text,
    TouchableWithoutFeedback,
    View,
    WithBadge
} from '../common';
import {
    EXPIRE_HIGHLIGHTER_TIME,
    ViewHighlight
} from '../common/ViewHighlight';
import {
    MODE,
    VERSION
} from '../config';
import { getTabBarOptions } from '../selectors';
import { tabBar as styles } from '../styles/components';
import { elementsStyles } from '../styles/elements';
import { head } from '../utils';

const activeTintColor = '#85A3E5';
const inactiveTintColor = '#8D9ABA';

const screenNameMap = {
    Home: 'home-page',
    MedCentersList: 'medcenter',
    SearchDrugs: 'pill',
    MyCard: 'report',
    MainFeedbacks: 'chart',
    Other: 'other',
};

/** Root stack screen per tab (`routes.js` Stack.Navigator initial/first screen). Used when re-tapping the active tab. */
const tabRootStackScreen = {
    Home: 'HomeRoute',
    MedCentersList: 'MedCentersListRoute',
    SearchDrugs: 'SearchDrugsRoute',
    MyCard: 'MyCard',
    MainFeedbacks: 'AllFeedbacks',
    Other: 'OtherRoute',
};

const Tab = ({
    routeName,
    isActive,
    count,
    color = isActive ? activeTintColor : inactiveTintColor,
    onPress,
    textId = `rootNavigation.${routeName}`,
    text = routeName === 'Other' && MODE !== 'prod' ? head(VERSION, '-') : Object.R(textId),
    ns = `${routeName}${isActive ? '__active' : ''}`,
}) => {
    return (
        <TouchableWithoutFeedback
            accessibilityLabel={ns}
            style={styles.tab}
            onPress={onPress}
        >
            <ViewHighlight
                style={styles.tab}
                highlightId={routeName}
                highlightStyle={styles.tabHighlight}
                expireTime={EXPIRE_HIGHLIGHTER_TIME}
                autoRemoveExpired={false}
                onExpire={() => {
                    actions.removeHighlighters([ 'panel', 'MyCard' ]);
                }}
            >
                <WithBadge notification={count}>
                    <Icon
                        name={screenNameMap[routeName]}
                        color={color}
                        onPress={onPress}
                    />
                </WithBadge>
                <Text
                    accessibilityLabel={textId}
                    style={[
                        elementsStyles.text,
                        {
                            fontSize: 9,
                            color,
                        },
                    ]}
                    numberOfLines={2}
                >
                    {text}
                </Text>
            </ViewHighlight>
        </TouchableWithoutFeedback>
    );
};

export const TabBar = entityData(getTabBarOptions, ({
    counts,
    navigation,
}) => {
    const {
        routes,
        index,
    } = navigation.getState();
    const insets = useSafeAreaInsets();
    return (
        <View
            style={[
                styles.safeArea,
                {
                    paddingLeft: insets.left,
                    paddingRight: insets.right,
                    paddingBottom: insets.bottom,
                },
            ]}
            forceInset={{
                bottom: 'always',
                top: 'never',
            }}
        >
            <View
                style={styles.tabBar}
                accessibilityLabel="tabBar">
                {routes.map(({
                    name,
                    key,
                }, idx) => (
                    <Tab
                        routeName={name.replace('Tab', '')}
                        onPress={() => {
                            if (index === idx) {
                                const root = tabRootStackScreen[name];
                                if (root) {
                                    navigation.navigate(name, {
                                        screen: root,
                                    });
                                } else if (navigation.popToTop) {
                                    navigation.popToTop();
                                }
                            } else {
                                navigation.navigate(name);
                            }
                        }}
                        key={key}
                        isActive={index === idx}
                        count={counts[key]}
                    />
                ))}
            </View>
        </View>
    );
});
